import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";
import { CustomEvents, subscribe, unsubscribe } from "../../events";
import {
  DEFAULT_LOCALE,
  LocalizedContent,
  SUPPORTED_LOCALES,
  SupportedLocale,
  ensureLocaleContent,
  findSupportedLocale,
  isLocaleContentLoaded,
  resolveContentBundle
} from "./content";

interface SiteContentContextValue {
  locale: SupportedLocale;
  content: LocalizedContent;
  setLocale: (nextLocale: SupportedLocale | string) => void;
  supportedLocales: SupportedLocale[];
}

const SiteContentContext = createContext<SiteContentContextValue | undefined>(
  undefined
);

const STORAGE_KEY = "marketing.site.locale";

const readLocaleFromQuery = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get("lang") ?? params.get("locale") ?? undefined;
};

const readLocaleFromStorage = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
};

export const determineInitialLocale = (explicit?: string): SupportedLocale => {
  // The URL first: ?lang= addresses a language version of the page (hreflang).
  const candidates = [readLocaleFromQuery(), explicit, readLocaleFromStorage()];

  for (const candidate of candidates) {
    const supported = findSupportedLocale(candidate);
    if (supported) {
      return supported;
    }
  }

  return resolveContentBundle().locale;
};

interface SiteContentProviderProps {
  initialLocale?: string;
  children: ReactNode;
}

export const SiteContentProvider: React.FC<SiteContentProviderProps> = ({
  initialLocale,
  children
}) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    determineInitialLocale(initialLocale)
  );

  const bundle = useMemo(
    () => resolveContentBundle(locale),
    [locale]
  );

  const setLocale = useCallback(
    (nextLocale: SupportedLocale | string) => {
      const wanted = findSupportedLocale(nextLocale);
      const apply = () => {
        const resolved = resolveContentBundle(nextLocale).locale;
        setLocaleState((current) => (resolved === current ? current : resolved));
      };
      // Content for other languages is a lazy chunk: fetch it, then switch.
      if (wanted && !isLocaleContentLoaded(wanted)) {
        void ensureLocaleContent(wanted).then(apply);
      } else {
        apply();
      }
    },
    []
  );

  useEffect(() => {
    if (!initialLocale) {
      return;
    }
    setLocale(initialLocale);
  }, [initialLocale, setLocale]);

  // The chosen language's text may have failed to load (English shows as the
  // fallback while the flag shows the choice): try again when the tab regains
  // focus or the connection comes back.
  useEffect(() => {
    const wanted = findSupportedLocale(initialLocale);
    if (!wanted || wanted === locale || typeof window === "undefined") {
      return;
    }
    const retry = () => setLocale(wanted);
    window.addEventListener("focus", retry);
    window.addEventListener("online", retry);
    return () => {
      window.removeEventListener("focus", retry);
      window.removeEventListener("online", retry);
    };
  }, [initialLocale, locale, setLocale]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = bundle.locale;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, bundle.locale);
    }
  }, [bundle.locale]);

  const value = useMemo(
    () => ({
      locale: bundle.locale,
      content: bundle.content,
      setLocale,
      supportedLocales: SUPPORTED_LOCALES
    }),
    [bundle.content, bundle.locale, setLocale]
  );

  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<string | { language?: string }>;
      const detail = customEvent.detail;
      const nextLocale = typeof detail === "string"
        ? detail
        : (detail && typeof (detail as any).language === "string"
            ? (detail as any).language
            : undefined);

      if (nextLocale) {
        setLocale(nextLocale);
      }
    };

    void subscribe(CustomEvents.languageChange, handleLanguageChange);

    return () => {
      void unsubscribe(CustomEvents.languageChange, handleLanguageChange);
    };
  }, [setLocale]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = (): SiteContentContextValue => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error("useSiteContent must be used within a SiteContentProvider");
  }
  return context;
};

export const useSupportedLocales = (): SupportedLocale[] => {
  return SUPPORTED_LOCALES;
};

export const useIsDefaultLocale = (): boolean => {
  const { locale } = useSiteContent();
  return locale === DEFAULT_LOCALE;
};
